import { Box, ButtonBase } from '@mui/material'
import {
  ChevronRight as ChevronIcon,
  VolumeUpOutlined as SpeakIcon,
} from '@mui/icons-material'
import { useState } from 'react'

import { colorTokens } from '@/apps/latest/shared/styles/colorTokens'
import { type CounselSource } from '@/features/counsel'
import { RADIUS } from '@/shared/styles/radii'
import {
  FONT_TABULAR,
  FONT_WEIGHT,
  ICON_SIZE,
  UI_FONT_SIZE,
} from '@/shared/styles/typography'

import { roundGhostSx } from './CounselSeat'
import { type SpeechPhase } from './useSpeech'

// 本文 16 × 1.6 = 25.6px。話者 (12px) と時刻 (11px) は line-height で本文の 1 行目に揃える
const BODY_LINE_HEIGHT = 1.6

interface Props {
  speaker: 'nagi' | 'あなた'
  // 作成中の行は文の代わりに状態のバッジを出す
  text?: string
  pending?: string
  // 相談者の行だけ。送った時刻 (往復に 1 つ)
  time?: string
  // 一緒に送った写真の名前
  attachment?: string
  // nagi の行だけ。調べた返事の出典
  sources?: CounselSource[]
  // nagi の行だけ。右端の読み上げ
  speech?: {
    phase?: SpeechPhase
    onToggle: () => void
  }
}

/**
 * 会話の行 (design-book 04-apps/kakeru.md がんの相談・executive-rag.md D9)。話者 (固定幅) / 本文 / 右端。
 * 右端は相談者の行が時刻、nagi の行が読み上げ。罫線もカード地色も持たず、余白と字格だけで区切る。
 * 出典は調べた返事だけが持ち、`参照した資料` の開閉に畳む (調べない回に空の器を置かない)。
 */
export const CounselLine = ({
  speaker,
  text,
  pending,
  time,
  attachment,
  sources,
  speech,
}: Props) => (
  <Box
    sx={{
      display: 'flex',
      alignItems: 'flex-start',
      gap: 1.25,
      py: 1,
    }}
  >
    <Box
      component="span"
      sx={{
        flex: 'none',
        width: '3em',
        whiteSpace: 'nowrap',
        fontSize: UI_FONT_SIZE.tag,
        fontWeight: FONT_WEIGHT.medium,
        lineHeight: 2.13,
        color: colorTokens.fg.muted,
      }}
    >
      {speaker}
    </Box>
    <Box sx={{ flex: 1, minWidth: 0 }}>
      {pending ? (
        <PendingBadge label={pending} />
      ) : (
        text && (
          <Box
            sx={{
              fontSize: UI_FONT_SIZE.body,
              lineHeight: BODY_LINE_HEIGHT,
              whiteSpace: 'pre-wrap',
              overflowWrap: 'anywhere',
              color: colorTokens.fg.default,
            }}
          >
            {text}
          </Box>
        )
      )}
      {attachment && (
        <Box
          sx={{
            mt: text ? '2px' : 0.5,
            fontSize: UI_FONT_SIZE.label,
            color: colorTokens.fg.muted,
            overflowWrap: 'anywhere',
          }}
        >
          {attachment}
        </Box>
      )}
      {sources && sources.length > 0 && <Sources sources={sources} />}
    </Box>
    {time && (
      <Box
        component="span"
        sx={{
          flex: 'none',
          ml: 1,
          fontSize: UI_FONT_SIZE.micro,
          lineHeight: 2.33,
          color: colorTokens.fg.faint,
          ...FONT_TABULAR,
        }}
      >
        {time}
      </Box>
    )}
    {speech && (
      <ButtonBase
        aria-label={speech.phase ? '読み上げを停止' : '読み上げ'}
        title={speech.phase ? '読み上げを停止' : '読み上げ'}
        onClick={speech.onToggle}
        sx={{
          ...roundGhostSx,
          mt: '2px',
          ...(speech.phase === 'busy' && { color: colorTokens.fg.faint }),
          ...(speech.phase === 'playing' && {
            backgroundColor: colorTokens.state.infoBg,
            color: colorTokens.state.successFg,
            '&:hover': { backgroundColor: colorTokens.state.infoBg },
          }),
        }}
      >
        {speech.phase ? <StopSquare size={8} /> : <SpeakIcon />}
      </ButtonBase>
    )}
  </Box>
)

// 鳴っている間・録っている間の停止の四角 (入力欄の mic の録音中と同じ対応)
export const StopSquare = ({ size }: { size: number }) => (
  <Box
    component="span"
    sx={{
      width: size,
      height: size,
      borderRadius: '2px',
      backgroundColor: 'currentColor',
    }}
  />
)

// 作成中のバッジ。完了は届いた行そのものが伝えるので、完了の通知は出さない
const PendingBadge = ({ label }: { label: string }) => (
  <Box
    component="span"
    sx={{
      display: 'inline-flex',
      alignItems: 'center',
      height: 28,
      px: 1.5,
      borderRadius: `${RADIUS.full}px`,
      backgroundColor: colorTokens.bg.muted,
      color: colorTokens.fg.muted,
      fontSize: UI_FONT_SIZE.tag,
      fontWeight: FONT_WEIGHT.medium,
      animation: 'counsel-pending 1.5s infinite',
      '@keyframes counsel-pending': {
        '0%, 100%': { opacity: 1 },
        '50%': { opacity: 0.5 },
      },
      '@media (prefers-reduced-motion: reduce)': { animation: 'none' },
    }}
  >
    {label}
  </Box>
)

// 出典の開閉。chevron が押せることと開いているかを言う (開時 90deg / 120ms)。hover に隠さない (スマホに hover が無い)
const Sources = ({ sources }: { sources: CounselSource[] }) => {
  const [isOpen, setIsOpen] = useState(false)
  return (
    <>
      <ButtonBase
        aria-expanded={isOpen}
        onClick={() => setIsOpen((prev) => !prev)}
        sx={{
          position: 'relative',
          gap: 1,
          mt: '2px',
          fontSize: UI_FONT_SIZE.tag,
          lineHeight: 1.45,
          color: colorTokens.fg.muted,
          '&:hover': { color: colorTokens.fg.default },
          '&.Mui-focusVisible': {
            boxShadow: colorTokens.focus.ringNeutral,
            borderRadius: `${RADIUS.sm}px`,
          },
          '@media (pointer: coarse)': {
            '&::after': {
              content: '""',
              position: 'absolute',
              left: 0,
              right: 0,
              top: '50%',
              height: 44,
              transform: 'translateY(-50%)',
            },
          },
        }}
      >
        <ChevronIcon
          sx={{
            fontSize: ICON_SIZE.sm,
            transition: 'transform 120ms',
            transform: isOpen ? 'rotate(90deg)' : 'none',
          }}
        />
        参照した資料
      </ButtonBase>
      {/* 中身は chevron の幅 + 間 (14 + 8) ぶん字下げする。開閉行より左に出ると行の中に見えない */}
      {isOpen && (
        <Box sx={{ pl: '22px' }}>
          {sources.map((source) => (
            <Box
              key={source.url}
              sx={{
                mt: 0.75,
                fontSize: UI_FONT_SIZE.tag,
                lineHeight: 1.4,
              }}
            >
              <Box
                component="a"
                href={source.url}
                target="_blank"
                rel="noopener noreferrer"
                sx={{
                  display: 'block',
                  color: colorTokens.fg.faint,
                  overflowWrap: 'anywhere',
                }}
              >
                {source.name}
              </Box>
              {source.quote && (
                <Box sx={{ color: colorTokens.fg.muted }}>
                  「{source.quote}」
                </Box>
              )}
            </Box>
          ))}
        </Box>
      )}
    </>
  )
}
